// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Circle from "src/assets/shapes/Circle/Circle";
import Square from "src/assets/shapes/Square/Square";

// Utils
import { getDecimalNum } from "src/utils/number/numberHelper";

// Types
import { ChartSpotType } from "src/services/exams/types";

const TOTAL_SPOTS = 120;

type Full120ChartLegendType = {
  full120Spots: Array<ChartSpotType>;
};

const Full120ChartLegend = ({ full120Spots }: Full120ChartLegendType) => {
  const { t } = useTranslation();

  const seenSpots = useMemo(
    () => full120Spots?.filter((point) => point?.seen),
    [full120Spots]
  );

  const efficiencyScore = useMemo(() => {
    const seenPercentage = (seenSpots?.length / TOTAL_SPOTS) * 100;

    // return an integer
    if (Number.isInteger(seenPercentage)) return seenPercentage;

    // return a floating point with 2 decimal places
    return getDecimalNum(seenPercentage, 2);
  }, [seenSpots?.length]);

  return (
    <div>
      <Box display="flex" alignItems="center" gap="8px">
        <Circle />
        <Typography fontSize="14px">
          <span style={{ fontWeight: 600 }}>{t("Seen")}:</span>{" "}
          {seenSpots?.length}/{TOTAL_SPOTS}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap="8px">
        <Square />
        <Typography fontSize="14px">
          <span style={{ fontWeight: 600 }}>{t("Not Seen")}:</span>{" "}
          {TOTAL_SPOTS - seenSpots?.length}/{TOTAL_SPOTS}
        </Typography>
      </Box>
      <Box display="flex">
        <Typography fontSize="14px">
          <span style={{ fontWeight: 600 }}>{t("Efficiency Score")}:</span>{" "}
          {efficiencyScore}
        </Typography>
      </Box>
    </div>
  );
};

export default Full120ChartLegend;
